import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Stack,
  Paper,
  Typography,
  Button,
  Alert,
  Tooltip,
} from '@mui/material';
import { CameraOff, Sparkles } from 'lucide-react';
import { CustomSelect } from '../../../../../../ui/custom_react_select/custom_select';
import { convertSecondsToTimeTaken } from '../../../../../../utils/helpers/time';
import { UserAssessment } from '../../../../../../utils/types/assessment_types';
import { SelectOptions } from '../../../../../../utils/types/react_select_types';

interface AssignedListModalProps {
  userAssessmentList: UserAssessment[];
  selectedUserAssessment: UserAssessment | undefined;
  setSelectedUserAssessment: (
    selectedAssessment: UserAssessment | undefined,
  ) => void;
  setModalContent: (content: number) => void;
  setIsPractice: (isPractice: boolean) => void;
  setAfterHelpModalContent: (content: number) => void;
}

export const AssignedListModal: React.FC<AssignedListModalProps> = ({
  userAssessmentList,
  selectedUserAssessment,
  setSelectedUserAssessment,
  setModalContent,
  setIsPractice,
  setAfterHelpModalContent,
}) => {
  const history = useHistory();
  const [selectedAssessment, setSelectedAssessment] = useState<
    SelectOptions | undefined
  >(
    selectedUserAssessment !== undefined
      ? {
        value: selectedUserAssessment?.assessment?.id,
        label: selectedUserAssessment?.assessment?.assessmentTitle,
      }
      : undefined,
  );

  const filteredAssessmentList = useMemo(() => {
    const CURRENT_TIME = Date.now() / 1000;
    return userAssessmentList.filter(
      (assessment) =>
        assessment.assessment.startDate < CURRENT_TIME &&
        assessment.assessment.endDate > CURRENT_TIME,
    );
  }, [userAssessmentList]);

  useEffect(() => {
    if (filteredAssessmentList.length === 1) {
      setSelectedUserAssessment(filteredAssessmentList[0]);
    }
  }, [filteredAssessmentList, setSelectedUserAssessment]);

  const assessmentOptions = useMemo(
    () =>
      filteredAssessmentList.map((assessment: UserAssessment) => ({
        value: assessment.assessment.id,
        label: assessment.assessment.assessmentTitle,
      })),
    [filteredAssessmentList],
  );

  const handleAssessmentSelect = (selected: SelectOptions) => {
    const foundUserAssessment = userAssessmentList.find(
      (userAssessment: UserAssessment) =>
        userAssessment.assessment.id === selected.value,
    );
    setSelectedUserAssessment(foundUserAssessment);
    setSelectedAssessment(selected);
  };

  const HELP_SEEN_KEY = 'hiacuity_help_seen';

  const handleStartFlow = (isPracticeMode: boolean) => {
    setIsPractice(isPracticeMode);

    // If starting a real assessment and there are multiple options, show details first
    if (!isPracticeMode && filteredAssessmentList.length > 1) {
      setModalContent(1);
      return;
    }

    const alreadySeen = localStorage.getItem(HELP_SEEN_KEY);
    if (!alreadySeen) {
      // First time — show help screens, then go to privacy (5)
      setAfterHelpModalContent(5);
      setModalContent(4);
    } else {
      // Already seen — go directly to privacy notice (5)
      setModalContent(5);
    }
  };

  // Stop all media streams when component mounts (after practice or assessment)
  useEffect(() => {
    const stopAllMediaStreams = () => {
      // Get all video and audio elements
      const videos = document.querySelectorAll('video');
      const audios = document.querySelectorAll('audio');

      // Stop all video streams
      videos.forEach((video) => {
        if (video.srcObject) {
          const stream = video.srcObject as MediaStream;
          stream.getTracks().forEach((track) => {
            track.stop();
          });
          video.srcObject = null;
        }
      });

      // Stop all audio streams
      audios.forEach((audio) => {
        if (audio.srcObject) {
          const stream = audio.srcObject as MediaStream;
          stream.getTracks().forEach((track) => {
            track.stop();
          });
          audio.srcObject = null;
        }
      });
    };

    stopAllMediaStreams();

    // Cleanup on unmount
    return () => {
      stopAllMediaStreams();
    };
  }, []);

  const firstName = localStorage.getItem('firstname') || 'Candidate';
  const hasNoProfilePic = localStorage.getItem('profilePic') === 'null';

  return (
    <Stack spacing={3} sx={{ width: '100%' }}>
      <Stack direction='row' alignItems='center' spacing={1}>
        <Typography variant='h5' fontWeight={700}>
          Welcome, {firstName}!
        </Typography>
        <Sparkles size={20} color='#f59e0b' />
      </Stack>

      {hasNoProfilePic && (
        <Alert severity='error'>
          You are not allowed to take the assessment until you have taken the registration photo.
        </Alert>
      )}

      {filteredAssessmentList.length === 1 && selectedUserAssessment ? (
        <Paper variant='outlined' sx={{ p: 3, borderRadius: 2 }}>
          <Stack spacing={1.5}>
            <Typography variant='subtitle1' fontWeight={600} color='text.primary'>
              Assessment Name: {selectedUserAssessment.assessment.assessmentTitle}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Assessment duration:{' '}
              {convertSecondsToTimeTaken(
                selectedUserAssessment.assessment.totalTime,
                false,
              )}{' '}
              minutes
            </Typography>
            {selectedUserAssessment.assessmentUser.status === 'In Progress' && (
              <Typography variant='body2' color='error.main' fontWeight={500}>
                Questions remaining:{' '}
                {selectedUserAssessment.assessmentUser.questionsRemaining}
              </Typography>
            )}
            <Typography variant='body2' color='text.secondary'>
              Assessment points: {selectedUserAssessment.assessment.totalScore} points
            </Typography>
          </Stack>
        </Paper>
      ) : filteredAssessmentList.length > 0 ? (
        <Box>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Here are the assignments assigned to you by HiAcuity
          </Typography>
          <Paper variant='outlined' sx={{ p: 2.5, borderRadius: 2 }}>
            <CustomSelect
              name='assessment'
              label='Select Assessment'
              options={assessmentOptions}
              value={selectedAssessment}
              onChange={(selectedOption: any) =>
                handleAssessmentSelect(selectedOption)
              }
            />
            {selectedUserAssessment && (
              <Stack spacing={1} sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                  <Typography variant='body2' color='text.secondary'>
                    Assessment duration:{' '}
                    {convertSecondsToTimeTaken(
                      selectedUserAssessment.assessment.totalTime,
                      false,
                    )}{' '}
                    minutes
                  </Typography>
                  {selectedUserAssessment.assessmentUser.status === 'In Progress' && (
                    <Typography variant='body2' color='error.main' fontWeight={500}>
                      Questions remaining:{' '}
                      {selectedUserAssessment.assessmentUser.questionsRemaining}
                    </Typography>
                  )}
                </Box>
                <Typography variant='body2' color='text.secondary'>
                  Assessment points: {selectedUserAssessment.assessment.totalScore} points
                </Typography>
              </Stack>
            )}
          </Paper>
        </Box>
      ) : (
        <Typography variant='body2' color='text.secondary'>
          No active assignments currently assigned
        </Typography>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems='center'
        justifyContent='space-between'
        spacing={2}
        sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}
      >
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Tooltip title='You can try a practice assessment'>
            <Button
              onClick={() => handleStartFlow(true)}
              variant='outlined'
              color='inherit'
            >
              Practice Tutorial
            </Button>
          </Tooltip>

          <Button
            onClick={() => {
              if (
                window.confirm(
                  'This will reload the page to force stop the camera and microphone. Continue?',
                )
              ) {
                const videos = document.querySelectorAll('video');
                const audios = document.querySelectorAll('audio');

                videos.forEach((video) => {
                  if (video.srcObject) {
                    const stream = video.srcObject as MediaStream;
                    stream.getTracks().forEach((track) => track.stop());
                    video.srcObject = null;
                  }
                });

                audios.forEach((audio) => {
                  if (audio.srcObject) {
                    const stream = audio.srcObject as MediaStream;
                    stream.getTracks().forEach((track) => track.stop());
                    audio.srcObject = null;
                  }
                });

                setTimeout(() => {
                  window.location.reload();
                }, 100);
              }
            }}
            variant='outlined'
            color='error'
            startIcon={<CameraOff size={16} />}
          >
            Turn Off Camera (Reload)
          </Button>
        </Stack>

        {userAssessmentList.length > 0 && (
          <Box>
            {hasNoProfilePic ? (
              <Button
                onClick={() => history.push('/identity-check')}
                variant='contained'
                color='primary'
              >
                Take Registration Photo
              </Button>
            ) : (
              <Tooltip title='You can attempt selected assessment'>
                <span>
                  <Button
                    onClick={() => handleStartFlow(false)}
                    variant='contained'
                    color='primary'
                    disabled={selectedUserAssessment === undefined}
                  >
                    Start Assessment
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </Stack>
    </Stack>
  );
};
